// Footer section: static contact card rendered at the bottom of the
// newsletter. Has no editable controls in the visual builder — its
// content is derived entirely from `tenantInfo`/`tenantName`.

import { safeUrl } from "@crm-src/lib/sanitize"
import { socialFooterLinksHtml } from "@crm-src/lib/email-social"
import type { NewsletterSection, TenantInfo } from "./stock-images"

interface FooterSectionProps {
  section: NewsletterSection
  tenantName: string
  tenantInfo?: TenantInfo
  year: number
}

export function FooterSection({
  tenantName,
  tenantInfo,
  year,
}: FooterSectionProps) {
  return (
    <div
      style={{
        backgroundColor: '#2d3748',
        padding: '17px 40px',
        color: '#ffffff',
        borderTop: '4px solid #6B8E23'
      }}
    >
      <table width="100%" cellPadding="0" cellSpacing="0">
        <tbody>
        <tr>
          <td style={{ textAlign: 'center', paddingBottom: '20px' }}>
            <h3 style={{ color: '#8FBC8F', fontSize: '18px', margin: '0 0 15px 0', fontWeight: 'bold' }}>
              Contact Us
            </h3>
          </td>
        </tr>
        <tr>
          <td>
            <table width="100%" cellPadding="0" cellSpacing="0">
              <tbody>
              <tr>
                <td style={{ width: '50%', verticalAlign: 'top', paddingRight: '20px' }}>
                  <p style={{ color: '#ffffff', fontSize: '16px', margin: '0 0 5px 0', fontWeight: 'bold' }}>
                    {tenantInfo?.name || tenantName}
                  </p>
                  {tenantInfo?.address && (
                    <p style={{ color: '#cbd5e0', fontSize: '14px', margin: '5px 0' }}>
                      {tenantInfo.address}
                    </p>
                  )}
                  {(tenantInfo?.city || tenantInfo?.state || tenantInfo?.postal_code) && (
                    <p style={{ color: '#cbd5e0', fontSize: '14px', margin: '5px 0' }}>
                      {[tenantInfo?.city, tenantInfo?.state, tenantInfo?.postal_code].filter(Boolean).join(', ')}
                    </p>
                  )}
                  {tenantInfo?.country && (
                    <p style={{ color: '#cbd5e0', fontSize: '14px', margin: '5px 0' }}>
                      {tenantInfo.country}
                    </p>
                  )}
                </td>
                <td style={{ width: '50%', verticalAlign: 'top', paddingLeft: '20px' }}>
                  {tenantInfo?.phone && (
                    <p style={{ color: '#cbd5e0', fontSize: '14px', margin: '5px 0' }}>
                      Phone: {tenantInfo.phone}
                    </p>
                  )}
                  {tenantInfo?.email && (
                    <p style={{ color: '#cbd5e0', fontSize: '14px', margin: '5px 0' }}>
                      Email: <a href={`mailto:${tenantInfo.email}`} style={{ color: '#8FBC8F', textDecoration: 'none' }}>{tenantInfo.email}</a>
                    </p>
                  )}
                  {tenantInfo?.website && (
                    <p style={{ color: '#cbd5e0', fontSize: '14px', margin: '5px 0' }}>
                      {/* `safeUrl` strips javascript:/data: schemes from user-provided websites in the preview */}
                      Web: <a href={safeUrl(tenantInfo.website)} style={{ color: '#8FBC8F', textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">{tenantInfo.website}</a>
                    </p>
                  )}
                </td>
              </tr>
              </tbody>
            </table>
          </td>
        </tr>
        <tr>
          <td style={{ textAlign: 'center', paddingTop: '30px', borderTop: '1px solid #4a5568', marginTop: '20px' }}>
            {/* One fixed follow-us block (LinkedIn + YouTube CTA buttons — see email-social.ts).
                Rendered from the same HTML the email emits so the preview stays WYSIWYG. */}
            <div dangerouslySetInnerHTML={{ __html: socialFooterLinksHtml({ theme: 'dark', marginStyle: 'margin:0 0 12px 0;' }) }} />
            <p style={{ color: '#a0aec0', fontSize: '12px', margin: '3px 0 0 0' }}>
              © {year} {tenantInfo?.name || tenantName}. All rights reserved.
            </p>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
  )
}

// HTML serializer used by `generateHtmlFromSections` in the orchestrator.
// Output is intentionally byte-identical to the original monolithic file
// — email clients are picky and even whitespace can affect rendering.
export function footerToHtml(
  _section: NewsletterSection,
  tenantName: string,
  tenantInfo: TenantInfo | undefined,
  year: number
): string {
  return `
          <!-- Footer -->
          <tr>
            <td style="background-color: #2d3748; padding: 17px 40px; color: #ffffff; border-top: 4px solid #6B8E23;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="text-align: center; padding-bottom: 20px;">
                    <h3 style="color: #8FBC8F; font-size: 18px; margin: 0 0 15px 0; font-weight: bold;">
                      Contact Us
                    </h3>
                  </td>
                </tr>
                <tr>
                  <td>
                    <table width="100%" cellpadding="0" cellspacing="0" border="0">
                      <tr>
                        <td style="width: 50%; vertical-align: top; padding-right: 20px;">
                          <p style="color: #ffffff; font-size: 16px; margin: 0 0 5px 0; font-weight: bold;">
                            ${tenantInfo?.name || tenantName}
                          </p>
                          ${tenantInfo?.address ? `
                          <p style="color: #cbd5e0; font-size: 14px; margin: 5px 0;">
                            ${tenantInfo.address}
                          </p>
                          ` : ''}
                          ${tenantInfo?.city || tenantInfo?.state || tenantInfo?.postal_code ? `
                          <p style="color: #cbd5e0; font-size: 14px; margin: 5px 0;">
                            ${[tenantInfo?.city, tenantInfo?.state, tenantInfo?.postal_code].filter(Boolean).join(', ')}
                          </p>
                          ` : ''}
                          ${tenantInfo?.country ? `
                          <p style="color: #cbd5e0; font-size: 14px; margin: 5px 0;">
                            ${tenantInfo.country}
                          </p>
                          ` : ''}
                        </td>
                        <td style="width: 50%; vertical-align: top; padding-left: 20px;">
                          ${tenantInfo?.phone ? `
                          <p style="color: #cbd5e0; font-size: 14px; margin: 5px 0;">
                            Phone: ${tenantInfo.phone}
                          </p>
                          ` : ''}
                          ${tenantInfo?.email ? `
                          <p style="color: #cbd5e0; font-size: 14px; margin: 5px 0;">
                            Email: <a href="mailto:${tenantInfo.email}" style="color: #8FBC8F; text-decoration: none;">${tenantInfo.email}</a>
                          </p>
                          ` : ''}
                          ${tenantInfo?.website ? `
                          <p style="color: #cbd5e0; font-size: 14px; margin: 5px 0;">
                            Web: <a href="${tenantInfo.website.startsWith('http') ? tenantInfo.website : 'https://' + tenantInfo.website}" style="color: #8FBC8F; text-decoration: none;" target="_blank">${tenantInfo.website}</a>
                          </p>
                          ` : ''}
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
                <tr>
                  <td style="text-align: center; padding-top: 30px; border-top: 1px solid #4a5568;">
                    ${socialFooterLinksHtml({ theme: "dark", marginStyle: "margin:15px 0 10px 0;" })}
                    <p style="color: #a0aec0; font-size: 12px; margin: 6px 0 0 0;">
                      © ${year} ${tenantInfo?.name || tenantName}. All rights reserved.
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
`
}
